import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, ref, type Ref, watch } from 'vue'
import { useOnboardingStore } from '~/store/onboarding'

/**
 * useOnboardingHost
 *
 * Central onboarding "host":
 * - Lazy-load the UI component (so it doesn't bloat the initial bundle)
 * - Start/goToStep for the active tour (respecting DOM/render timing)
 * - Keeps the overlay aligned with the layout scroll container
 *
 * How to use in the layout:
 * - Pass the ref to the scroll container (e.g. mainContentRef)
 * - Render AsyncOnboardingWrapper only when shouldRenderOnboarding is true
 * - Bind steps/options from the store and handle @finish with handleOnboardingFinish
 */
export function useOnboardingHost(scrollContainerRef: Ref<HTMLElement | null>) {
  const onboardingStore = useOnboardingStore()

  // Lazy-load: only download v-onboarding/popper when there is a pending/active tour
  const AsyncOnboardingWrapper = defineAsyncComponent(() => import('~/components/onboarding/OnboardingWrapper.vue'))

  interface OnboardingWrapperExposed {
    start: () => void
    finish: () => void
    goToStep: (newStepNumber: number | ((currentStepNumber: number) => number)) => void
  }

  const onboardingWrapperRef = ref<OnboardingWrapperExposed | null>(null)

  const shouldRenderOnboarding = computed(() => onboardingStore.isActive || onboardingStore.queue.length > 0)

  const isTourUiActive = ref(false)
  let scrollRafId: number | null = null
  let clickLockApplied = false
  let wheelProxyApplied = false
  let touchProxyApplied = false
  let lastTouchX: number | null = null
  let lastTouchY: number | null = null

  /**
   * Blocks pointer/click events outside the onboarding step.
   * Allows interaction only inside `.onboarding-step`.
   */
  function preventPointer(event: Event) {
    if (!isTourUiActive.value)
      return
    const target = event.target as HTMLElement | null
    if (target && target.closest('.onboarding-step'))
      return
    event.stopImmediatePropagation()
    event.stopPropagation()
    event.preventDefault()
  }

  /** Installs the click lock (capture-level listeners). */
  function applyClickLock() {
    if (clickLockApplied) return
    clickLockApplied = true
    window.addEventListener('pointerdown', preventPointer, true)
    window.addEventListener('click', preventPointer, true)
    window.addEventListener('auxclick', preventPointer, true)
  }

  /** Removes the click lock. */
  function releaseClickLock() {
    if (!clickLockApplied) return
    clickLockApplied = false
    window.removeEventListener('pointerdown', preventPointer, true)
    window.removeEventListener('click', preventPointer, true)
    window.removeEventListener('auxclick', preventPointer, true)
  }

  /**
   * When the user scrolls over the onboarding popover, force that scroll to happen
   * on the page scroll container (never inside the overlay/popover).
   */
  function proxyWheelToPage(event: WheelEvent) {
    if (!isTourUiActive.value) {
      return
    }

    // Preserve browser zoom gesture (trackpad pinch / ctrl+wheel).
    if (event.ctrlKey) {
      return
    }

    // Prevent the browser from scrolling any internal overlay container.
    event.preventDefault()

    const scrollContainer = scrollContainerRef.value
    if (scrollContainer) {
      scrollContainer.scrollTop += event.deltaY
      scrollContainer.scrollLeft += event.deltaX
      handleScroll()
      return
    }

    window.scrollBy({ top: event.deltaY, left: event.deltaX })
    handleScroll()
  }

  function applyWheelProxy() {
    if (wheelProxyApplied) return
    wheelProxyApplied = true
    window.addEventListener('wheel', proxyWheelToPage, { capture: true, passive: false })
  }

  function releaseWheelProxy() {
    if (!wheelProxyApplied) return
    wheelProxyApplied = false
    window.removeEventListener('wheel', proxyWheelToPage, true)
  }

  function proxyTouchStart(event: TouchEvent) {
    if (!isTourUiActive.value) return
    const firstTouch = event.touches[0]
    if (!firstTouch) return
    lastTouchX = firstTouch.clientX
    lastTouchY = firstTouch.clientY
  }

  function proxyTouchMove(event: TouchEvent) {
    if (!isTourUiActive.value) return
    const firstTouch = event.touches[0]
    if (!firstTouch) return

    if (lastTouchX === null || lastTouchY === null) {
      lastTouchX = firstTouch.clientX
      lastTouchY = firstTouch.clientY
      return
    }

    const deltaX = lastTouchX - firstTouch.clientX
    const deltaY = lastTouchY - firstTouch.clientY
    lastTouchX = firstTouch.clientX
    lastTouchY = firstTouch.clientY

    // Prevent the browser from scrolling internal overlay containers.
    event.preventDefault()

    const scrollContainer = scrollContainerRef.value
    if (scrollContainer) {
      scrollContainer.scrollTop += deltaY
      scrollContainer.scrollLeft += deltaX
      handleScroll()
      return
    }

    window.scrollBy({ top: deltaY, left: deltaX })
    handleScroll()
  }

  function applyTouchProxy() {
    if (touchProxyApplied) return
    touchProxyApplied = true
    window.addEventListener('touchstart', proxyTouchStart, { capture: true, passive: true })
    window.addEventListener('touchmove', proxyTouchMove, { capture: true, passive: false })
  }

  function releaseTouchProxy() {
    if (!touchProxyApplied) return
    touchProxyApplied = false
    window.removeEventListener('touchstart', proxyTouchStart, true)
    window.removeEventListener('touchmove', proxyTouchMove, true)
    lastTouchX = null
    lastTouchY = null
  }

  /** Forces popper/overlay recalculation on resize/scroll. */
  function refreshOverlay() {
    window.dispatchEvent(new Event('resize'))
    window.dispatchEvent(new Event('scroll'))
  }

  /** Throttles overlay refresh to animation frames while tour UI is active. */
  function handleScroll() {
    if (!isTourUiActive.value) {
      return
    }

    if (scrollRafId !== null) {
      return
    }

    scrollRafId = window.requestAnimationFrame(() => {
      scrollRafId = null
      refreshOverlay()
    })
  }

  /**
   * Polls for a stable DOMRect for the target element (waits until it has size).
   */
  async function waitForStableRect(element: Element, attempts = 20, intervalMs = 50) {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const rect = element.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        return true
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }
    return false
  }

  /**
   * Starts the active tour when possible:
   * - ensures wrapper is mounted
   * - finds the first step with a valid target
   * - waits for target to have size, then jumps to that step
   */
  async function startActiveTourIfPossible() {
    // The component is lazy-loaded: it may not be mounted yet.
    if (!onboardingWrapperRef.value) {
      setTimeout(() => {
        if (onboardingStore.activeTourId) {
          startActiveTourIfPossible()
        }
      }, 150)
      return
    }

    const steps = onboardingStore.activeSteps
    if (!steps.length) {
      return
    }

    isTourUiActive.value = true
    applyClickLock()
    applyWheelProxy()
    applyTouchProxy()
    onboardingWrapperRef.value.start()

    await nextTick()

    // Start at the first step whose attachTo target exists in the DOM.
    const firstIndex = steps.findIndex((step) => {
      const attachTo = step.attachTo.element
      if (typeof attachTo === 'string') {
        return Boolean(document.querySelector(attachTo))
      }
      return Boolean(attachTo())
    })

    if (firstIndex < 0) {
      // The DOM may not have rendered the target yet (e.g. async content).
      setTimeout(() => {
        if (onboardingStore.activeTourId) {
          startActiveTourIfPossible()
        }
      }, 150)
      return
    }

    const attachTo = steps[firstIndex].attachTo.element
    const attachableElement = typeof attachTo === 'string' ? document.querySelector(attachTo) : attachTo()
    if (!attachableElement) {
      return
    }

    await waitForStableRect(attachableElement)
    onboardingWrapperRef.value.goToStep(firstIndex)
    refreshOverlay()
  }

  /**
   * Handles tour finish:
   * - unlocks scroll/clicks
   * - tells the store to finish the active tour
   */
  function handleOnboardingFinish() {
    isTourUiActive.value = false
    releaseClickLock()
    releaseWheelProxy()
    releaseTouchProxy()
    onboardingStore.finishActiveTour()
  }

  /**
   * Step 1: when a tour becomes active, try to start it.
   */
  watch(() => onboardingStore.activeTourId, async (tourId) => {
    if (!tourId) {
      isTourUiActive.value = false
      releaseWheelProxy()
      releaseTouchProxy()
      return
    }

    await nextTick()
    setTimeout(() => {
      startActiveTourIfPossible()
    }, 50)
  }, { immediate: true })

  /**
   * Step 2: wire up the layout scroll container (where the user actually scrolls).
   */
  watch(scrollContainerRef, (el, previousEl) => {
    if (previousEl) {
      previousEl.removeEventListener('scroll', handleScroll)
    }
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true })
    }
  }, { immediate: true })

  onBeforeUnmount(() => {
    if (scrollContainerRef.value) {
      scrollContainerRef.value.removeEventListener('scroll', handleScroll)
    }
    releaseWheelProxy()
    releaseTouchProxy()
    if (scrollRafId !== null) {
      window.cancelAnimationFrame(scrollRafId)
    }
  })

  return {
    // Component
    AsyncOnboardingWrapper,

    // Refs/flags
    onboardingWrapperRef,
    shouldRenderOnboarding,

    // Props for wrapper
    activeSteps: computed(() => onboardingStore.activeSteps),
    activeOptions: computed(() => onboardingStore.activeOptions),

    // Events
    handleOnboardingFinish,
  }
}
